
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, { ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FormDataContext, Plan, Protections } from "../lib/FormDataContext";
import { Plans } from "shared/config/enums/formEnums";
import { Parameter, QuotePayload } from 'shared/api/assurant/quotePayloadTypes';
import { mockPayload } from 'shared/api/assurant/quote';
import { useMutationQuery, useQuoteQuery } from 'entities/Quote/hooks/queries';
import { QuoteResponse } from 'shared/api/assurant/quoteResponseTypes';
import { ApplicationFormContext } from 'app/providers/applicationForm/lib/ApplicationFormContext';
import { IFormICheckoutInput } from '../../../../shared/config/types/checkout';

export interface IFormInput {
	plan: {
		period: Plans,
		price: string,
		installmentAmount: string,
		fee: string | null
	},
	liability: string | undefined | null
	belongings: number
	deductible: string
}

interface Props {
	children?: ReactNode
}

const floodIncludedStates = ['CT', 'NJ', 'NY', 'TX']

const FormDataProvider = ({ children }: Props) => {
	const refInitialData = useRef<boolean>(true);
	const { mutate, data, isPending } = useMutationQuery()

	const [plan, setPlan] = useState<Plan>({
		period: Plans.ANNUAL,
		installmentAmount: '',
		downPaymentAmount:  '',
		fee: ''
	});

	const query = useQuoteQuery()
	const { applicationFormData } = useContext(ApplicationFormContext);
	const [defaultQuery, setDefaultQuery] = useState<QuoteResponse | undefined>(undefined)
	const [policyStartDate, setPolicyStartDate] = useState('')
	const [leaseEffectiveDate, setLeaseEffectiveDate] = useState<string | undefined>()
	const [policyState, setPolicyState] = useState('FL')
	const [formId, setFormId] = useState('')
	const [schemaAddress, setSchemaAddress] = useState({
		City: '',
		State: '',
		FullAddress: '',
		Address1: '',
		Address2: '',
		ZipCode: '',
	})
	const [formData, setFormData] = useState<IFormInput>({
		plan: {
			period: Plans.ANNUAL,
			price: '',
			installmentAmount: '',
			fee: '',
		},
		liability: '',
		deductible: '',
		belongings: 5000
	});
	const [protections, setProtections] = useState<Protections>({
		hasReplacementCost: 'Y',
		hasEarthquake: 'N',
		hasIdFraud: 'Y',
		hasWaterBackup: 'Y',
		hasWaterDamageLiability: 'Y',
	});
	const [initialPayloadData, setInitialPayloadData] = useState<QuotePayload>(mockPayload);
	const [paymentData, setPaymentData] = useState<IFormICheckoutInput>();

	const defaultProps = useMemo(() => ({
		isPending,
		formData,
		setFormData,
		protections,
		setProtections,
		initialPayload: initialPayloadData,
		setInitialPayloadData,
		query,
		defaultQuery,
		setDefaultQuery,
		policyStartDate,
		plan,
		setPlan,
		setPolicyStartDate,
		setLeaseEffectiveDate,
		policyState,
		setPolicyState,
		schemaAddress,
		setSchemaAddress,
		formId,
		setFormId,
		paymentData,
		setPaymentData,
		refInitialData: refInitialData,
	}), [formData, setFormData, protections, setProtections, initialPayloadData, setInitialPayloadData, query, defaultQuery, policyStartDate,
		setPolicyStartDate, setLeaseEffectiveDate, policyState, setPolicyState, schemaAddress, setSchemaAddress, formId, setFormId, isPending, plan,
		setPlan, paymentData, setPaymentData]);

	// Helper function to build payload from applicationFormData
	const buildPayloadFromApplicationForm = (appFormData: typeof applicationFormData) => {
		if (!appFormData) return null;

		const underwritingData = appFormData.underwriting.questions.reduce<Record<string, string>>((accum, item) => {
			accum[item.questionName] = item.answer
			return accum
		}, {})

		let params = appFormData.coverage_info.parameters as Parameter[]

		if (appFormData.property_address.state === 'NC') {
			params = [...params, { name: 'hasWaterDamageLiability', value: 'Y' } as Parameter]
		}

		if (floodIncludedStates.includes(appFormData.property_address.state)) {
			params = [...params, { name: 'floodIncluded', value: 'Y' } as Parameter]
		}
		const newParams = params.reduce<Record<string, string | number>>((accum, item) => {

			accum[item.name] = item.value
			return accum
		}, {})

		setProtections(newParams)
		let questions = [
			...appFormData.underwriting.questions,
			{
				"questionName": "dwellingType",
				"answer": underwritingData?.dwellingType
			},
			{
				"questionName": "howManyLosses",
				"answer": underwritingData?.howManyLosses
			},
			{
				"questionName": "lastLossDate",
				"answer": underwritingData?.lastLossDate
			},
			{
				"questionName": "isManufacturedHome",
				"answer": underwritingData?.isManufacturedHome
			},
		]

		if(leaseEffectiveDate){
			questions = [
				...questions,
				{
					"questionName": "leaseEffectiveDate",
					"answer": leaseEffectiveDate
				}
			]
		}

		return {
			coverage: {
				parameters: params,
			},
			transaction: {
				productType: appFormData.quote_result.transactionDetails.productId,
				clientId: appFormData.quote_result.transactionDetails.clientId,
				workItemId: appFormData.quote_result.transactionDetails.workItemId,
				jobNumber: "000932",
				referralId: appFormData.quote_result.transactionDetails.referralId,
				policyEffectiveDate: policyStartDate
			},
			policyHolder: {
				insured: {
					address: {
						address1: schemaAddress.Address1,
						address2: schemaAddress.Address2,
						city: schemaAddress.City,
						state: schemaAddress.State,
						postalCode: schemaAddress.ZipCode,
					}
				},
			},
			underWriting: {
				questions: questions
			}
		}
	}

	// Update data when quote response changes
	useEffect(() => {
		if (data?.data) {
			setDefaultQuery(data.data);
			const currentProperty = initialPayloadData.coverage.parameters.find((item) => item.name === 'personalPropertyAmount');
			const waterBackup = initialPayloadData.coverage.parameters.find((item) => item.name === 'waterBackup');
			const belongingChosen = data.data.coverageDetails.parameters.find((item) => item.name === 'personalPropertyAmount');
			const waterBackupChosen = data.data.coverageDetails.parameters.find((item) => item.name === 'waterBackup');

			if (
				(belongingChosen?.value && currentProperty?.value !== belongingChosen.value) ||
				(waterBackupChosen?.value && waterBackup?.value !== waterBackupChosen.value)
			) {
				const newParametersData = initialPayloadData.coverage.parameters.map((item) => {
					if (item.name === 'personalPropertyAmount' && belongingChosen) {
						return { ...item, value: String(belongingChosen.value) }
					}
					if (item.name === 'waterBackup' && waterBackupChosen) {
						return { ...item, value: String(waterBackupChosen.value) }
					}
					return item
				})

				setInitialPayloadData({
					...initialPayloadData,
					coverage: {
						parameters: newParametersData
					}
				})
			}
		}
	}, [data]);

	// If quote data is already present in the cache (e.g., from the customer journey fetch), use it to seed bounds/UI.
	useEffect(() => {
		if (query && !defaultQuery) {
			setDefaultQuery(query)
		}
	}, [query, defaultQuery]);

	// If the stored personal property amount is outside the bounds returned by Assurant, clamp it into range.
	useEffect(() => {
		if (!defaultQuery) return
		const defaults = { min: 5000, max: 95000, step: 5000 }
		const bounds = defaultQuery.coveragesCue.parameters.reduce<Record<'min' | 'max' | 'step', number>>((accum, item) => {
			if (item.name === 'personalPropertyMin' && typeof item.value === 'string') {
				accum.min = Number(item.value)
			} else if (item.name === 'personalPropertyMax' && typeof item.value === 'string') {
				accum.max = Number(item.value)
			} else if (item.name === 'personalPropertyIncrement' && typeof item.value === 'string') {
				accum.step = Number(item.value)
			}
			return accum
		}, defaults)

		const currentParam = initialPayloadData.coverage.parameters.find((p) => p.name === 'personalPropertyAmount')
		if (!currentParam) return
		const parsed = Number(currentParam.value)
		if (!Number.isFinite(parsed)) return
		const clamped = Math.min(bounds.max, Math.max(bounds.min, parsed))
		if (clamped === parsed) return
		setInitialPayloadData((prev) => {
			const existing = prev.coverage.parameters.find((p) => p.name === 'personalPropertyAmount')
			const existingParsed = existing ? Number(existing.value) : NaN
			if (!Number.isFinite(existingParsed) || existingParsed === clamped) return prev
			return {
				...prev,
				coverage: {
					parameters: prev.coverage.parameters.map((p) => (p.name === 'personalPropertyAmount' ? { ...p, value: String(clamped) } : p)),
				},
			}
		})
	}, [defaultQuery, initialPayloadData.coverage.parameters, setInitialPayloadData])

	// Initialize payload when applicationFormData is first received
	useEffect(() => {
		if (refInitialData.current && applicationFormData) {
			const newPayload = buildPayloadFromApplicationForm(applicationFormData);

			if (newPayload) {
				setInitialPayloadData({
					...newPayload,
					transaction: {
						...newPayload.transaction,
						workItemId: applicationFormData.quote_result.transactionDetails.workItemId
					}
				});
			}

			refInitialData.current = false;
		}
	}, [applicationFormData]);

	// Call createNext after initialPayload is set AND whenever it changes
	useEffect(() => {
		const hasValidData = !refInitialData.current &&
			applicationFormData &&
			initialPayloadData.transaction.clientId &&
			schemaAddress.Address1;

		if (hasValidData) {
			mutate({
				...initialPayloadData,
				transaction: {
					...initialPayloadData.transaction,
					policyEffectiveDate: policyStartDate,
					...(leaseEffectiveDate ? { leaseEffectiveDate: leaseEffectiveDate } : {}),
				},
				policyHolder: {
					...initialPayloadData.policyHolder,
					insured: {
						address: {
							...initialPayloadData.policyHolder.insured.address,
							address1: schemaAddress.Address1,
							address2: schemaAddress.Address2,
							city: schemaAddress.City,
							state: schemaAddress.State,
							postalCode: schemaAddress.ZipCode,
						}
					},
				},
				underWriting: {
					questions: [
						...initialPayloadData.underWriting.questions,
					]
				}
			})
		}
	}, [
		initialPayloadData, // This will trigger when initialPayloadData changes
		applicationFormData,
		schemaAddress.Address1,
		mutate,
		policyStartDate,
		leaseEffectiveDate
	]);

	return (
		<FormDataContext.Provider value={defaultProps}>
			{children}
		</FormDataContext.Provider>
	)
}

export default FormDataProvider
